// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Six machines in a row, and how a player gets between them (M5-20, M5-21, M5-23).
//
// The arithmetic of the venue, with no Three.js in it: where each bay is, where a player stands
// at one, which bay they are looking at, and which one a teleport lands on. It is separated from
// the geometry because it is the part that decides things — the geometry only draws them — and
// because "did the pinch teleport me to the bay I was looking at" is a question worth being able
// to answer in a test rather than by putting a headset on.
//
// **Smooth locomotion is not offered and this file is the reason it does not have to be.** 06 §4:
// "Pinball is a stationary game; adding a movement system would add motion-sickness risk for no
// gameplay value." A player looks at a bay and pinches. Between bays there is nothing to walk
// past, because a row of machines against a wall is the entire venue.
//
// **A bay is a place to stand, not a machine.** The distinction matters for presence: a remote
// player's avatar is drawn at *their* bay's standing position, and the pose they stream is in the
// playfield-local frame of the machine they are at (06 §4), so two players in two different real
// rooms line up correctly against the same machine.

import { yawFacing } from './placement'

/** One machine's place in the room. Metres, room space, +Y up. */
export interface Bay {
  readonly index: number
  /** Where the cabinet's origin sits. */
  readonly x: number
  readonly z: number
  /** Which way the cabinet faces — the yaw a `Placement` takes. */
  readonly yaw: number
  /** Where a player stands to play it: in front of the lockdown bar, facing the machine. */
  readonly standX: number
  readonly standZ: number
}

/** How many bays the venue has (06 §4). */
export const BAY_COUNT = 6

/**
 * Distance between bay centres, metres.
 *
 * 1.15. A cabinet is 0.724 wide, so this leaves 0.43 m between neighbours — which is about what a
 * real arcade leaves, and is deliberately *tight*. A row of machines with generous gaps reads as
 * a showroom; a row you have to turn sideways to walk along reads as an arcade.
 */
export const BAY_SPACING = 1.15

/** How far in front of the cabinet origin a player stands, metres. */
export const STAND_OFFSET = 0.95

/**
 * How far back from the room's centre line the row sits, metres.
 *
 * **Named because the venue is a space two clients have to agree about.** `BAY_SPACING` and
 * `STAND_OFFSET` are exported and this was a bare literal inside `buildBays`, which is the one
 * member of the trio the cross-stack parity check could not see. The two stacks do agree — the
 * Quest calls it `ArcadeFloor.RowZ` — and a venue where they did not would put every remote
 * avatar
 * a row's depth away from the machine they are standing at, on the other person's screen only.
 */
export const ROW_Z = -1.6

/**
 * The row.
 *
 * Centred on the room's origin and running along +X, all facing the same way — a row against a
 * wall, which is where arcade machines go, because the back of a pinball cabinet is not a thing
 * anybody puts on display.
 */
export function buildBays(count = BAY_COUNT, spacing = BAY_SPACING): readonly Bay[] {
  const bays: Bay[] = []
  const first = -((count - 1) * spacing) / 2
  for (let i = 0; i < count; i++) {
    const x = first + i * spacing
    const z = ROW_Z
    const standZ = z + STAND_OFFSET
    // The cabinet's front is its local +Z (see `Placement.yaw`), so a machine at the far wall
    // with the player in front of it at greater Z faces along +Z: yaw 0. Computed rather than
    // written down, so a venue that ever puts a bay somewhere else still faces its player.
    bays.push({ index: i, x, z, yaw: yawFacing(x, z, x, standZ), standX: x, standZ })
  }
  return bays
}

/**
 * The widest angle off a bay that still counts as looking at it, radians.
 *
 * 0.28 rad ≈ 16°. Wide enough that a player does not have to aim, narrow enough that two adjacent
 * bays are never both candidates from the standing position — at 1.15 m apart and 0.95 m back,
 * neighbouring bays are 50° apart from where a player stands, so there is a large margin.
 */
export const LOOK_CONE = 0.28

/**
 * Which bay a head at `(x, z)` looking along `yaw` is aimed at, or undefined.
 *
 * `yaw` is the head's facing in room space, measured the same way a `Placement`'s is: 0 looks
 * down −Z. Angular distance rather than a ray cast, because a ray needs geometry and a look cone
 * needs two numbers — and because the answer has to be stable while the player's head drifts,
 * which an angle naturally is and a ray against a cabinet's collision box is not.
 */
export function bayLookedAt(
  bays: readonly Bay[],
  x: number,
  z: number,
  yaw: number,
  cone = LOOK_CONE,
): Bay | undefined {
  let best: Bay | undefined
  let bestOff = cone
  for (const bay of bays) {
    const dx = bay.x - x
    const dz = bay.z - z
    if (dx === 0 && dz === 0) continue
    const toBay = Math.atan2(dx, -dz)
    const off = Math.abs(normalise(toBay - yaw))
    if (off < bestOff) {
      bestOff = off
      best = bay
    }
  }
  return best
}

/** Wrap an angle into −π … π. */
export function normalise(a: number): number {
  let r = a
  while (r > Math.PI) r -= 2 * Math.PI
  while (r < -Math.PI) r += 2 * Math.PI
  return r
}

/**
 * Where the player is standing, and where they are going.
 *
 * A teleport is instantaneous, which is the whole point — a fade would be a movement system with
 * extra steps. What this holds is which bay is current, so the venue knows which machine is
 * *the* machine and which five are running attract mode.
 */
export class ArcadeFloor {
  readonly bays: readonly Bay[]
  private current: number

  constructor(bays: readonly Bay[] = buildBays(), startAt = Math.floor(BAY_COUNT / 2)) {
    this.bays = bays
    this.current = clampIndex(startAt, bays.length)
  }

  get bay(): Bay {
    return this.bays[this.current] as Bay
  }

  get index(): number {
    return this.current
  }

  /** Every bay that is not the player's — the ones running attract mode (M5-21). */
  neighbours(): readonly Bay[] {
    return this.bays.filter((b) => b.index !== this.current)
  }

  /** Teleport. Returns false when the bay is already the current one, so callers can stay quiet. */
  goTo(index: number): boolean {
    const next = clampIndex(index, this.bays.length)
    if (next === this.current) return false
    this.current = next
    return true
  }

  /** Look at a bay and pinch. Returns the bay moved to, or undefined if nothing was aimed at. */
  teleportToLookedAt(x: number, z: number, yaw: number): Bay | undefined {
    const target = bayLookedAt(this.bays, x, z, yaw)
    if (target === undefined) return undefined
    return this.goTo(target.index) ? target : undefined
  }
}

function clampIndex(i: number, length: number): number {
  if (!Number.isFinite(i)) return 0
  const n = Math.trunc(i)
  return n < 0 ? 0 : n >= length ? length - 1 : n
}
